<template>
  <div ref="chart" class="echarts-sales-heatmap" role="img" :aria-label="ariaLabel" />
</template>

<script>
import echarts, { chartAnimationDuration } from './echarts-core'

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const HOURS = Array.from({ length: 24 }, (_, hour) => {
  if (hour === 0) return '12a'
  if (hour === 12) return '12p'
  return hour > 12 ? `${hour - 12}p` : `${hour}a`
})

export default {
  name: 'EChartsSalesHeatmap',
  props: {
    cells: { type: Array, required: true },
    ariaLabel: { type: String, default: 'Sales activity heatmap' },
    isDark: { type: Boolean, default: false }
  },
  data() {
    return { chart: null, resizeObserver: null }
  },
  mounted() {
    this.$nextTick(this.initChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.refresh)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.chart) this.chart.dispose()
  },
  watch: {
    cells: { handler() { this.updateChart() }, deep: true },
    isDark() { this.updateChart() }
  },
  methods: {
    initChart() {
      if (!this.$refs.chart || this.chart) return
      this.chart = echarts.init(this.$refs.chart, null, { renderer: 'canvas' })
      window.addEventListener('resize', this.refresh)
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.refresh)
        this.resizeObserver.observe(this.$refs.chart)
      }
      this.updateChart()
    },
    refresh() {
      this.$nextTick(() => this.chart && this.chart.resize())
    },
    updateChart() {
      if (!this.chart) return this.$nextTick(this.initChart)
      const textColor = this.isDark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)'
      const borderColor = this.isDark ? '#232323' : '#ffffff'
      const data = this.cells.map(cell => ({
        value: [Number(cell.hour), Number(cell.dayIndex), Number(cell.orders || 0)],
        revenue: Number(cell.revenue || 0),
        units: Number(cell.units || 0)
      }))
      const maxOrders = Math.max(1, ...data.map(point => point.value[2]))
      this.chart.setOption({
        animationDuration: chartAnimationDuration(),
        grid: { top: 8, right: 18, bottom: 58, left: 10, containLabel: true },
        tooltip: {
          position: 'top',
          renderMode: 'richText',
          confine: true,
          backgroundColor: this.isDark ? 'rgba(35,35,35,0.96)' : 'rgba(20,20,20,0.88)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: point => {
            if (!point?.data) return ''
            const [hour, day, orders] = point.data.value
            return [
              `${DAYS[day]} ${HOURS[hour]}`,
              `Orders: ${orders}`,
              `Revenue: $${point.data.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              `Units: ${point.data.units}`
            ].join('\n')
          }
        },
        xAxis: {
          type: 'category',
          data: HOURS,
          splitArea: { show: true },
          axisLabel: { color: textColor, interval: 1, fontSize: 10 },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'category',
          data: DAYS,
          inverse: true,
          splitArea: { show: true },
          axisLabel: { color: textColor, fontSize: 11 },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        visualMap: {
          min: 0,
          max: maxOrders,
          calculable: false,
          orient: 'horizontal',
          left: 'center',
          bottom: 2,
          text: ['More orders', 'Fewer'],
          textStyle: { color: textColor, fontSize: 10 },
          inRange: { color: this.isDark ? ['#172826', '#2e6d59', '#4ADE80'] : ['#edf4f1', '#a8c8ba', '#0d2d2c'] }
        },
        series: [{
          name: 'Orders',
          type: 'heatmap',
          data,
          itemStyle: { borderColor, borderWidth: 2, borderRadius: 2 },
          emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.22)' } }
        }]
      }, true)
      this.refresh()
    }
  }
}
</script>

<style scoped>
.echarts-sales-heatmap { width: 100%; height: 100%; min-height: 300px; }
</style>
